import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useCampusAuth } from "@/contexts/CampusAuth";
import { COURSE_LIST } from "@/data/courses";
import {
  Users, UserPlus, Search, Save, KeyRound, Copy, ShieldCheck, X, Loader2, Unlock,
  GraduationCap, FileSignature, Download, Mail,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_LABELS = {
  "Pflege-KI Kompakt": "Pflege-KI Kompakt · bis 10 MA · 199 €/Monat",
  "Pflege-KI Team": "Pflege-KI Team · ab 11 MA · 349 €/Monat",
  "PDL-Trial": "PDL-Trial · 14 Tage kostenlos",
  "Admin": "Admin · Vollzugriff",
};

const ROLE_LABELS = {
  pflegekraft: "Pflegekraft",
  pdl: "PDL",
  stell_pdl: "Stellv. PDL",
  leitung: "Leitung",
  inhaber: "Inhaber:in",
  andere: "Andere",
};

export default function AdminUserManagement() {
  const { user } = useCampusAuth();
  const [data, setData] = useState({ users: [], allowed_tiers: [], allowed_roles: [] });
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [tierFilter, setTierFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [credModal, setCredModal] = useState(null); // {email, password, isReset}

  const load = useCallback(async () => {
    if (!user?.is_admin) return;
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (tierFilter) params.tier = tierFilter;
      const r = await axios.get(`${API}/admin/users-mgmt`, {
        withCredentials: true,
        params,
      });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  }, [user, q, tierFilter]);

  useEffect(() => { load(); }, [load]);

  if (!user?.is_admin) {
    return (
      <div data-testid="users-mgmt-locked" className="rounded-3xl border border-cream-300 bg-white p-8">
        <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-brand-darkred mb-2">Admin only</div>
        <p className="text-sand-700">Diese Funktion ist Admins vorbehalten.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-users-mgmt-page">
      {/* Header */}
      <div className="rounded-3xl border border-cream-300 bg-white p-7 shadow-soft">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-brand-darkred mb-2 inline-flex items-center gap-2">
              <Users className="h-3.5 w-3.5" /> User-Verwaltung
            </div>
            <h1 className="font-display text-3xl text-sand-800 font-normal tracking-tight">
              Nutzer anlegen & Tier zuweisen
            </h1>
            <p className="text-sand-600 text-sm mt-2 max-w-2xl">
              Lege neue Pflege-KI-Nutzer:innen an oder ändere bestehende Tiers. Audit-Log wird automatisch geschrieben.
            </p>
          </div>
          <Button
            onClick={() => setShowCreate(true)}
            data-testid="open-create-user"
            className="rounded-full bg-terra hover:bg-terra-dark text-white px-6 h-11 font-semibold inline-flex items-center gap-2"
          >
            <UserPlus className="h-4 w-4" />
            Neuen Nutzer anlegen
          </Button>
        </div>

        {/* Filter */}
        <div className="mt-5 flex flex-wrap gap-3 items-center border-t border-cream-300 pt-5">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-sand-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Suche · E-Mail oder Name"
              data-testid="users-mgmt-search"
              className="w-full rounded-xl border border-cream-300 bg-cream-50 pl-10 pr-4 py-2.5 text-sm outline-none focus:border-terra"
            />
          </div>
          <select
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value)}
            data-testid="users-mgmt-tier-filter"
            className="rounded-xl border border-cream-300 bg-cream-50 px-4 py-2.5 text-sm outline-none focus:border-terra"
          >
            <option value="">Alle Tiers</option>
            {data.allowed_tiers.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <span className="text-xs text-sand-500 ml-auto">
            {loading ? "Lädt…" : `${data.count ?? 0} Nutzer angezeigt`}
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-3xl border border-cream-300 bg-white shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-cream-100 border-b border-cream-300">
              <tr>
                <th className="text-left px-5 py-3 font-semibold text-sand-700 text-xs uppercase tracking-wider">E-Mail / Name</th>
                <th className="text-left px-5 py-3 font-semibold text-sand-700 text-xs uppercase tracking-wider">Rolle</th>
                <th className="text-left px-5 py-3 font-semibold text-sand-700 text-xs uppercase tracking-wider">Tier</th>
                <th className="text-left px-5 py-3 font-semibold text-sand-700 text-xs uppercase tracking-wider">Status</th>
                <th className="text-right px-5 py-3 font-semibold text-sand-700 text-xs uppercase tracking-wider">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {(data.users || []).map((u) => (
                <UserRow key={u.id} u={u} allowedTiers={data.allowed_tiers} allowedRoles={data.allowed_roles} onChanged={load} onReset={(payload) => setCredModal(payload)} />
              ))}
              {!loading && (data.users || []).length === 0 && (
                <tr><td colSpan={5} className="px-5 py-12 text-center text-sand-500 text-sm">Keine Nutzer gefunden.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showCreate && (
        <CreateUserModal
          allowedTiers={data.allowed_tiers}
          allowedRoles={data.allowed_roles}
          onClose={() => setShowCreate(false)}
          onCreated={(payload) => {
            setShowCreate(false);
            setCredModal(payload);
            load();
          }}
        />
      )}

      {credModal && <CredentialsModal payload={credModal} onClose={() => setCredModal(null)} />}
    </div>
  );
}

function UserRow({ u, allowedTiers, allowedRoles, onChanged, onReset }) {
  const [tier, setTier] = useState(u.tier);
  const [role, setRole] = useState(u.role);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [showCourseDone, setShowCourseDone] = useState(false);
  const [showDocs, setShowDocs] = useState(false);
  const dirty = tier !== u.tier || role !== u.role;

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/users-mgmt/${u.id}`,
        { tier, role },
        { withCredentials: true },
      );
      toast.success(`${u.email} aktualisiert.`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const resetPassword = async () => {
    if (!window.confirm(`Passwort für ${u.email} wirklich zurücksetzen?`)) return;
    setResetting(true);
    try {
      const r = await axios.post(`${API}/admin/users-mgmt/${u.id}/reset-password`, {},
        { withCredentials: true });
      onReset?.({ email: u.email, password: r.data.new_password, isReset: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset fehlgeschlagen");
    } finally {
      setResetting(false);
    }
  };

  const unlockAccount = async () => {
    setUnlocking(true);
    try {
      const r = await axios.post(`${API}/admin/users-mgmt/${u.id}/unlock`, {},
        { withCredentials: true });
      const cleared = r.data?.cleared_attempts ?? 0;
      if (cleared > 0) {
        toast.success(`Konto entsperrt – ${cleared} Fehlversuch(e) gelöscht.`);
      } else {
        toast.message("Konto war nicht gesperrt.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Entsperren fehlgeschlagen");
    } finally {
      setUnlocking(false);
    }
  };

  return (
    <tr className="border-b border-cream-200 last:border-0 hover:bg-cream-50/50" data-testid={`user-row-${u.id}`}>
      <td className="px-5 py-3">
        <div className="font-medium text-sand-800">{u.name || "—"}</div>
        <div className="text-xs text-sand-600">{u.email}</div>
        {u.is_admin && (
          <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-brand-darkred text-cream-50 text-[10px] font-bold uppercase tracking-wider">
            <ShieldCheck className="h-3 w-3" /> Admin
          </span>
        )}
      </td>
      <td className="px-5 py-3">
        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          data-testid={`user-role-select-${u.id}`}
          className="rounded-lg border border-cream-300 bg-cream-50 px-3 py-1.5 text-xs"
        >
          {(allowedRoles || []).map((r) => (
            <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
          ))}
        </select>
      </td>
      <td className="px-5 py-3">
        <select
          value={tier}
          onChange={(e) => setTier(e.target.value)}
          data-testid={`user-tier-select-${u.id}`}
          className="rounded-lg border border-cream-300 bg-cream-50 px-3 py-1.5 text-xs font-semibold"
        >
          {!(allowedTiers || []).includes(u.tier) && (
            <option value={u.tier}>{u.tier} (Legacy)</option>
          )}
          {(allowedTiers || []).map((t) => <option key={t} value={t}>{TIER_LABELS[t] || t}</option>)}
        </select>
      </td>
      <td className="px-5 py-3 text-xs text-sand-600">
        {u.email_verified ? (
          <span className="inline-flex items-center gap-1 text-sage-dark">✓ verifiziert</span>
        ) : (
          <span className="text-clay">unbestätigt</span>
        )}
      </td>
      <td className="px-5 py-3 text-right">
        <div className="inline-flex gap-2 flex-wrap justify-end">
          <button
            type="button"
            onClick={() => setShowCourseDone(true)}
            data-testid={`user-course-done-${u.id}`}
            className="inline-flex items-center gap-1 rounded-full bg-terra hover:bg-terra-dark text-white px-3 py-1.5 text-xs font-semibold transition-colors"
            title="Kurs als beendet markieren – Teilnehmer erhält E-Mail zum Unterschreiben"
          >
            <GraduationCap className="h-3 w-3" />
            Kurs beendet
          </button>
          <button
            type="button"
            onClick={() => setShowDocs(true)}
            data-testid={`user-docs-${u.id}`}
            className="inline-flex items-center gap-1 rounded-full border border-cream-400 hover:border-terra px-3 py-1.5 text-xs font-medium text-sand-700"
            title="Unterschriebene Dokumente dieses Teilnehmers"
          >
            <FileSignature className="h-3 w-3" />
            Doku
          </button>
          <button
            type="button"
            onClick={unlockAccount}
            disabled={unlocking}
            data-testid={`user-unlock-${u.id}`}
            className="inline-flex items-center gap-1 rounded-full border border-cream-400 hover:border-terra px-3 py-1.5 text-xs font-medium text-sand-700 disabled:opacity-50"
            title="Konto entsperren (bei 429-Sperre nach zu vielen Fehlversuchen)"
          >
            {unlocking ? <Loader2 className="h-3 w-3 animate-spin" /> : <Unlock className="h-3 w-3" />}
            Entsperren
          </button>
          <button
            type="button"
            onClick={resetPassword}
            disabled={resetting}
            data-testid={`user-reset-pw-${u.id}`}
            className="inline-flex items-center gap-1 rounded-full border border-cream-400 hover:border-clay px-3 py-1.5 text-xs font-medium text-sand-700 disabled:opacity-50"
            title="Passwort zurücksetzen (hebt auch Konto-Sperre auf)"
          >
            {resetting ? <Loader2 className="h-3 w-3 animate-spin" /> : <KeyRound className="h-3 w-3" />}
            Reset
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            data-testid={`user-save-${u.id}`}
            className="inline-flex items-center gap-1 rounded-full bg-sand-800 disabled:bg-cream-300 disabled:text-sand-500 text-cream-50 px-3 py-1.5 text-xs font-semibold transition-colors"
          >
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
            Speichern
          </button>
        </div>
        {showCourseDone && (
          <CourseDoneModal user={u} onClose={() => setShowCourseDone(false)} />
        )}
        {showDocs && (
          <SignaturesModal user={u} onClose={() => setShowDocs(false)} />
        )}
      </td>
    </tr>
  );
}

function CourseDoneModal({ user: u, onClose }) {
  const [courseId, setCourseId] = useState(COURSE_LIST[0]?.id || "");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const course = COURSE_LIST.find((c) => c.id === courseId);

  const submit = async () => {
    if (!course) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/signatures/request`, {
        email: u.email,
        name: u.name || u.email,
        course_id: course.id,
        course_title: course.title,
        modules: course.curriculum || [],
      }, { withCredentials: true });
      setResult(r.data);
      if (r.data.email_sent) {
        toast.success(`E-Mail zum Unterschreiben an ${u.email} versendet.`);
      } else {
        toast.warning("Anfrage gespeichert, aber E-Mail konnte nicht versendet werden.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Abschließen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] bg-sand-800/60 backdrop-blur-sm flex items-center justify-center p-4 text-left" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="course-done-modal"
        className="bg-white rounded-3xl shadow-hover max-w-lg w-full p-7 font-sans font-normal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-brand-darkred mb-1">Kurs beendet</div>
            <h2 className="font-display text-2xl text-sand-800">{u.name || u.email}</h2>
          </div>
          <button type="button" onClick={onClose} className="text-sand-500 hover:text-sand-800"><X className="h-5 w-5" /></button>
        </div>

        {result ? (
          <div data-testid="course-done-result">
            <div className="rounded-2xl border border-sage/40 bg-sage/10 p-4 text-sm text-sand-700 mb-4">
              <div className="font-semibold text-sage-dark mb-1 inline-flex items-center gap-1.5">
                <Mail className="h-4 w-4" /> {result.email_sent ? "E-Mail versendet" : "E-Mail NICHT versendet"}
              </div>
              <p>
                Alle {result.modules?.length || 0} Module von <strong>{result.course_title}</strong> gelten als bearbeitet.
                {result.email_sent
                  ? ` ${u.email} wurde gebeten, die Teilnahmebestätigung zu unterschreiben.`
                  : " Bitte Link manuell weitergeben:"}
              </p>
              {!result.email_sent && (
                <code className="block mt-2 text-[11px] break-all bg-white rounded-lg p-2 border border-cream-300">{result.link}</code>
              )}
            </div>
            <div className="flex justify-end">
              <Button onClick={onClose} className="rounded-full bg-terra hover:bg-terra-dark text-white px-6">Fertig</Button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-sand-600 mb-4 leading-relaxed">
              Wähle den abgeschlossenen Kurs. Alle Module werden als <strong>bearbeitet</strong> markiert
              und der Teilnehmer erhält eine E-Mail mit dem Link zum <strong>Unterschreiben</strong> der Teilnahmebestätigung.
            </p>
            <label className="block mb-3">
              <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-sand-500 mb-1.5 block">Kurs *</span>
              <select value={courseId} onChange={(e) => setCourseId(e.target.value)}
                data-testid="course-done-select"
                className="w-full rounded-xl border border-cream-300 bg-cream-50 px-4 py-2.5 text-sm outline-none focus:border-terra">
                {COURSE_LIST.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            </label>
            {course?.curriculum?.length > 0 && (
              <div className="rounded-2xl bg-cream-100 border border-cream-300 p-4 mb-5 max-h-40 overflow-y-auto">
                <div className="text-[10px] uppercase tracking-wider font-bold text-sand-500 mb-2">{course.curriculum.length} Module werden als bearbeitet markiert</div>
                <ul className="text-xs text-sand-600 space-y-1 list-disc pl-4">
                  {course.curriculum.map((m, i) => <li key={i}>{m}</li>)}
                </ul>
              </div>
            )}
            <div className="flex gap-3 justify-end">
              <Button type="button" variant="ghost" onClick={onClose} className="rounded-full">Abbrechen</Button>
              <Button onClick={submit} disabled={busy} data-testid="course-done-submit"
                className="rounded-full bg-terra hover:bg-terra-dark text-white px-6 inline-flex items-center gap-2">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <GraduationCap className="h-4 w-4" />}
                Kurs beendet & E-Mail senden
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SignaturesModal({ user: u, onClose }) {
  const [items, setItems] = useState(null);
  const [downloading, setDownloading] = useState("");

  useEffect(() => {
    axios.get(`${API}/admin/signatures`, { params: { email: u.email }, withCredentials: true })
      .then((r) => setItems(r.data.items || []))
      .catch(() => { toast.error("Dokumente konnten nicht geladen werden"); setItems([]); });
  }, [u.email]);

  const downloadPdf = async (sig) => {
    setDownloading(sig.id);
    try {
      const r = await axios.get(`${API}/admin/signatures/${sig.id}/pdf`, {
        withCredentials: true, responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Teilnahmebestaetigung-${(u.name || u.email).replace(/[^A-Za-z0-9]/g, "-")}-${sig.course_id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF-Download fehlgeschlagen");
    } finally {
      setDownloading("");
    }
  };

  return (
    <div className="fixed inset-0 z-[110] bg-sand-800/60 backdrop-blur-sm flex items-center justify-center p-4 text-left" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="signatures-modal"
        className="bg-white rounded-3xl shadow-hover max-w-xl w-full p-7 font-sans font-normal max-h-[85vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-brand-darkred mb-1">Unterschriebene Dokumente</div>
            <h2 className="font-display text-2xl text-sand-800">{u.name || u.email}</h2>
          </div>
          <button type="button" onClick={onClose} className="text-sand-500 hover:text-sand-800"><X className="h-5 w-5" /></button>
        </div>

        {items === null && <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-sand-500" /></div>}
        {items?.length === 0 && (
          <p className="text-sm text-sand-500 py-8 text-center" data-testid="signatures-empty">
            Noch keine Teilnahmebestätigungen für diesen Teilnehmer.<br />
            Nutze „Kurs beendet", um eine Unterschrift anzufordern.
          </p>
        )}
        <div className="space-y-3">
          {(items || []).map((s) => (
            <div key={s.id} data-testid={`signature-item-${s.id}`}
              className="rounded-2xl border border-cream-300 bg-cream-50 p-4 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-semibold text-sand-800 text-sm">{s.course_title}</div>
                <div className="text-xs text-sand-500 mt-0.5">
                  Angefordert: {s.created_at?.slice(0, 10)}
                  {s.signed_at && <> · Unterschrieben: {s.signed_at.slice(0, 10)}{s.learn_mode_label ? ` · ${s.learn_mode_label}` : ""}</>}
                </div>
              </div>
              {s.status === "signed" ? (
                <button type="button" onClick={() => downloadPdf(s)} disabled={downloading === s.id}
                  data-testid={`signature-pdf-${s.id}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-sand-800 text-cream-50 px-4 py-2 text-xs font-semibold hover:bg-sand-700 disabled:opacity-50">
                  {downloading === s.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                  PDF
                </button>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full bg-saffron/20 text-sand-700 border border-saffron/50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider">
                  Ausstehend
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CreateUserModal({ allowedTiers, allowedRoles, onClose, onCreated }) {
  const [form, setForm] = useState({
    email: "",
    name: "",
    role: "pdl",
    tier: "Pflege-KI Kompakt",
    organisation: "",
    password: "",
    is_admin: false,
    send_welcome_email: true,
  });
  const [busy, setBusy] = useState(false);
  const isPdlTrial = form.tier === "PDL-Trial";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/users-mgmt`, form, { withCredentials: true });
      onCreated?.({
        email: r.data.email,
        password: r.data.initial_password,
        tier: r.data.tier,
        name: r.data.name,
        welcome_email_sent: r.data.welcome_email_sent,
        trial_expires_at: r.data.trial_expires_at,
        trial_max_kundenakten: r.data.trial_max_kundenakten,
        isReset: false,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Anlegen fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-sand-800/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl shadow-hover max-w-lg w-full p-7 max-h-[90vh] overflow-y-auto"
        data-testid="create-user-modal">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h2 className="font-display text-2xl text-sand-800 font-normal">Neuen Nutzer anlegen</h2>
            <p className="text-xs text-sand-600 mt-1">Passwort wird automatisch generiert, wenn leer gelassen.</p>
          </div>
          <button type="button" onClick={onClose} className="text-sand-500 hover:text-sand-800">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <Field label="E-Mail *" required>
            <input
              type="email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="create-user-email"
              className="w-full rounded-xl border border-cream-300 bg-cream-50 px-4 py-2.5 text-sm outline-none focus:border-terra"
            />
          </Field>
          <Field label="Vollständiger Name *" required>
            <input
              type="text" required minLength={2} value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="create-user-name"
              className="w-full rounded-xl border border-cream-300 bg-cream-50 px-4 py-2.5 text-sm outline-none focus:border-terra"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Rolle *">
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                data-testid="create-user-role"
                className="w-full rounded-xl border border-cream-300 bg-cream-50 px-3 py-2.5 text-sm">
                {allowedRoles.map((r) => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
              </select>
            </Field>
            <Field label="Tier *">
              <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })}
                data-testid="create-user-tier"
                className="w-full rounded-xl border border-cream-300 bg-cream-50 px-3 py-2.5 text-sm font-semibold">
                {allowedTiers.map((t) => <option key={t} value={t}>{TIER_LABELS[t] || t}</option>)}
              </select>
            </Field>
          </div>

          {(form.tier === "Pflege-KI Kompakt" || form.tier === "Pflege-KI Team") && (
            <div
              data-testid="tier-info-box"
              className="rounded-2xl border border-terra/30 bg-terra/[0.06] p-4 text-sm text-sand-700"
            >
              <div className="text-[10.5px] uppercase tracking-[0.24em] font-bold text-brand-darkred mb-1.5">
                {form.tier === "Pflege-KI Kompakt"
                  ? "Paket Klein · bis 10 Mitarbeiter · 199 €/Monat"
                  : "Paket Groß · ab 11 Mitarbeiter · 349 €/Monat"}
              </div>
              <ul className="list-disc pl-5 space-y-0.5 text-[13px] leading-snug">
                <li>Voller Pflege-KI-Zugriff · unbegrenzt Patientenmappen</li>
                <li>
                  {form.tier === "Pflege-KI Kompakt"
                    ? <><strong>1 PDL + bis zu 10 Mitarbeiter</strong>-Sub-Accounts</>
                    : <><strong>1 PDL + unbegrenzt Mitarbeiter</strong>-Sub-Accounts</>}
                </li>
                <li>SIS · Risikomatrix · Medikation · QPR-26 · PDF-Export · Halluzinationsschutz</li>
              </ul>
            </div>
          )}

          {isPdlTrial && (
            <div
              data-testid="pdl-trial-info-box"
              className="rounded-2xl border border-terra/30 bg-terra/[0.06] p-4 text-sm text-sand-700"
            >
              <div className="text-[10.5px] uppercase tracking-[0.24em] font-bold text-brand-darkred mb-1.5">
                PDL-Trial · 14 Tage kostenlos
              </div>
              <ul className="list-disc pl-5 space-y-0.5 text-[13px] leading-snug">
                <li><strong>5 Kundenakten</strong> · Vollzugriff PDL-Suite</li>
                <li>SIS · Maßnahmenplan · Pflegebericht · NBA · Kassenbrief · Höherstufung</li>
                <li><strong>MD-Simulator V1 &amp; V2</strong> inklusive</li>
                <li>Nach 14 Tagen: Account deaktiviert · Upgrade auf <strong>349 €/Monat</strong> möglich</li>
              </ul>
            </div>
          )}

          <Field label="Pflegedienst / Organisation (optional)">
            <input
              type="text" value={form.organisation}
              onChange={(e) => setForm({ ...form, organisation: e.target.value })}
              data-testid="create-user-org"
              className="w-full rounded-xl border border-cream-300 bg-cream-50 px-4 py-2.5 text-sm outline-none focus:border-terra"
            />
          </Field>
          <Field label="Initial-Passwort (optional)" hint="Leer lassen → wir generieren ein sicheres Passwort">
            <input
              type="text" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="create-user-password"
              placeholder="Wird automatisch generiert"
              className="w-full rounded-xl border border-cream-300 bg-cream-50 px-4 py-2.5 text-sm outline-none focus:border-terra font-mono"
            />
          </Field>
          <div className="space-y-2 pt-1">
            <label className="flex items-center gap-2 text-sm text-sand-700 cursor-pointer">
              <input type="checkbox" checked={form.send_welcome_email}
                onChange={(e) => setForm({ ...form, send_welcome_email: e.target.checked })}
                data-testid="create-user-send-welcome" />
              Welcome-Mail direkt versenden
              {isPdlTrial && (
                <span className="text-[11px] font-medium text-brand-darkred">
                  (bei PDL-Trial immer aktiv)
                </span>
              )}
            </label>
            <label className="flex items-center gap-2 text-sm text-sand-700 cursor-pointer">
              <input type="checkbox" checked={form.is_admin}
                onChange={(e) => setForm({ ...form, is_admin: e.target.checked })}
                data-testid="create-user-is-admin" />
              Als Admin anlegen (Vollzugriff)
            </label>
          </div>
        </div>

        <div className="mt-6 flex gap-3 justify-end border-t border-cream-300 pt-5">
          <Button type="button" variant="ghost" onClick={onClose} className="rounded-full">Abbrechen</Button>
          <Button type="submit" disabled={busy}
            data-testid="create-user-submit"
            className="rounded-full bg-terra hover:bg-terra-dark text-white px-6 inline-flex items-center gap-2">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
            Nutzer anlegen
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-sand-500 mb-1.5 block">{label}</span>
      {children}
      {hint && <span className="text-[11px] text-sand-500 mt-1 block">{hint}</span>}
    </label>
  );
}

function CredentialsModal({ payload, onClose }) {
  const copyText = `E-Mail: ${payload.email}\nPasswort: ${payload.password}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(copyText);
      toast.success("Zugangsdaten kopiert");
    } catch {
      toast.error("Kopieren fehlgeschlagen");
    }
  };
  const isTrial = payload.tier === "PDL-Trial" && !!payload.trial_expires_at;
  const trialUntil = isTrial
    ? new Date(payload.trial_expires_at).toLocaleDateString("de-DE", {
        day: "2-digit", month: "long", year: "numeric",
      })
    : null;
  return (
    <div className="fixed inset-0 z-[101] bg-sand-800/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl shadow-hover max-w-lg w-full p-7"
        data-testid="credentials-modal">
        <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-brand-darkred mb-2">
          {payload.isReset ? "Passwort zurückgesetzt" : "Nutzer angelegt"}
        </div>
        <h2 className="font-display text-2xl text-sand-800 mb-4">Zugangsdaten</h2>
        <div className="bg-cream-100 rounded-2xl p-5 border border-cream-300 space-y-2 font-mono text-sm">
          <div><span className="text-sand-500 text-xs uppercase tracking-wider">E-Mail:</span> <strong>{payload.email}</strong></div>
          <div><span className="text-sand-500 text-xs uppercase tracking-wider">Passwort:</span> <strong>{payload.password}</strong></div>
          {payload.tier && (
            <div><span className="text-sand-500 text-xs uppercase tracking-wider">Tier:</span> <strong>{payload.tier}</strong></div>
          )}
        </div>
        {isTrial && (
          <div
            data-testid="trial-summary"
            className="mt-4 rounded-2xl border border-terra/30 bg-terra/[0.06] p-4 text-sm text-sand-700"
          >
            <div className="text-[10.5px] uppercase tracking-[0.24em] font-bold text-brand-darkred mb-1.5">
              PDL-Trial aktiv
            </div>
            <div className="text-[13px] leading-relaxed">
              Gültig bis <strong>{trialUntil}</strong> · max. <strong>{payload.trial_max_kundenakten || 5} Kundenakten</strong>.
              Danach: Deaktivierung → Upgrade auf PDL Suite Solo (349 €/Monat).
            </div>
          </div>
        )}
        {payload.welcome_email_sent === true && (
          <p data-testid="welcome-email-status" className="text-xs text-sage-dark mt-3">
            ✓ Welcome-Mail wurde an <strong>{payload.email}</strong> versendet.
          </p>
        )}
        {payload.welcome_email_sent === false && (
          <p data-testid="welcome-email-status" className="text-xs text-clay mt-3">
            ⚠ Welcome-Mail konnte nicht versendet werden — bitte Zugangsdaten manuell weitergeben.
          </p>
        )}
        <p className="text-xs text-clay mt-4 leading-relaxed">
          ⚠️ Dieses Passwort wird <strong>nur ein einziges Mal</strong> angezeigt. Bitte sofort kopieren und sicher an die Person übermitteln.
          Beim ersten Login bitte selbst ändern lassen.
        </p>
        <div className="mt-5 flex gap-3 justify-end">
          <button type="button" onClick={copy}
            data-testid="copy-credentials"
            className="inline-flex items-center gap-2 rounded-full border-2 border-sand-800 hover:bg-sand-800 hover:text-cream-50 px-5 py-2 text-sm font-semibold transition-colors">
            <Copy className="h-4 w-4" /> Zugangsdaten kopieren
          </button>
          <Button onClick={onClose} className="rounded-full bg-terra hover:bg-terra-dark text-white px-6">
            Verstanden
          </Button>
        </div>
      </div>
    </div>
  );
}
